import React, { FC as ReactFC, useEffect, useMemo, useState } from 'react';

import moment from 'moment';
import * as intl from 'react-intl-universal';
import ReactTooltip from 'react-tooltip';
import { Button, Row } from 'reactstrap';

import TrustedDeviceCols from 'shared/enums/TrustedDeviceCols';
import { TrustedDevice } from 'shared/modules/users/containers/user-profile-view/UserProfileViewState';

import styles from '../mfaTrustedDevices.module.scss';
import MfaTrustedDevicesListItem from './mfa-trusted-devices-list-item/MfaTrustedDevicesListItem';
import MfaTrustedDevicesListProps from './MfaTrustedDevicesListProps';

const MfaTrustedDevicesList: ReactFC<MfaTrustedDevicesListProps> = (
  props: MfaTrustedDevicesListProps
) => {
  const {
    trustedDevices,
    status,
    onRefetchTrustedDevices,
    onRevokeTrustedDevice,
  } = props;

  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [sortColumn, setSortColumn] = useState<string>('os');

  const sortedData = useMemo(() => {
    // Create a shallow copy to avoid mutating the original prop array
    const dataToSort = [...trustedDevices];

    dataToSort.sort((a, b) => {
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];

      let comparison = 0;

      // Date comparison
      if (sortColumn === 'createdAt' || sortColumn === 'expiresAt') {
        const dateA = moment(aValue);
        const dateB = moment(bValue);
        if (!dateA.isValid() || !dateB.isValid()) {
          comparison = 0;
        } else {
          comparison = dateA.valueOf() - dateB.valueOf(); // Compare timestamps
        }
      }
      // Case-insensitive string comparison
      else if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
      }
      // Otherwise, use default comparison
      else if (aValue < bValue) {
        comparison = -1;
      } else if (aValue > bValue) {
        comparison = 1;
      }

      return sortDirection === 'asc' ? comparison : comparison * -1;
    });

    return dataToSort;
  }, [trustedDevices, sortColumn, sortDirection]);

  useEffect(() => {
    ReactTooltip.rebuild();
  }, [trustedDevices]);

  const columns = [
    {
      order: 1,
      key: TrustedDeviceCols.OS,
      header: intl.get('LBL_MFA_TRUSTED_DEVICES_OS_HEADER'),
      mapsTo: 'os' as keyof TrustedDevice,
    },
    {
      order: 2,
      key: TrustedDeviceCols.BROWSER,
      header: intl.get('LBL_MFA_TRUSTED_DEVICES_BROWSER_HEADER'),
      mapsTo: 'browser' as keyof TrustedDevice,
    },
    {
      order: 3,
      key: TrustedDeviceCols.CREATION_DATE,
      header: intl.get('LBL_MFA_TRUSTED_DEVICES_CREATION_DATE_HEADER'),
      mapsTo: 'createdAt' as keyof TrustedDevice,
    },
    {
      order: 4,
      key: TrustedDeviceCols.STATUS,
      header: intl.get('LBL_MFA_TRUSTED_DEVICES_STATUS_HEADER'),
      mapsTo: 'expiresAt' as keyof TrustedDevice,
    },
  ];

  const handleHeaderClick = (columnKey: keyof TrustedDevice) => {
    // If clicking the same column, toggle direction
    if (columnKey === sortColumn) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    }
    // If clicking a new column, set it and default to ascending
    else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  return (
    <div className={styles.wrap}>
      {trustedDevices.length > 3 ? <span className={styles.track} /> : ''}
      {trustedDevices.length > 0 ? (
        <div
          className={`insight-table-container-query ${styles.scroll} ${
            trustedDevices.length > 3 ? styles.addRightPadding : ''
          }`}
        >
          <table className={`insight-table ${styles.table}`}>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    onClick={() => handleHeaderClick(column.mapsTo)}
                    className={
                      sortColumn === column.mapsTo ? styles.active : ''
                    }
                  >
                    <span className="d-inline-flex align-items-center">
                      {column.header}
                      {sortColumn === column.mapsTo && (
                        <i
                          className={`icon-chevron-${
                            sortDirection === 'asc' ? 'up' : 'down'
                          } ml-2`}
                        />
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedData.map((device) => (
                <MfaTrustedDevicesListItem
                  key={device.id}
                  device={device}
                  status={status}
                  onRevokeTrustedDevice={onRevokeTrustedDevice}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className={`no-data-message ${styles.emptyAlert}`}>
          <div className="title">{intl.get('ERR_CHART_EMPTY_TITLE')}</div>
          <div>{intl.get('ERR_MFA_TRUSTED_DEVICES_EMPTY_DESC')}</div>
          <hr className="divider" />
          <Row className="w-100 justify-content-center m-0">
            <Button
              className="btn btn-secondary"
              onClick={onRefetchTrustedDevices}
            >
              <i className="icon-refresh" />
              {intl.get('BTN_SURVEYS_DRAFTED_EMPTY_RETRY')}
            </Button>
          </Row>
        </div>
      )}
    </div>
  );
};

export default MfaTrustedDevicesList;
